import type { Scene } from "@babylonjs/core/scene";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { ZoneState } from "@arena/shared";
import { ANTONIETTA, ANTONIETTA_BRIOCHE } from "@arena/character-data";

/** Size of the brioche drawn upright (its 48-pixel sprite), in metres. */
const BRIOCHE_SIZE = 0.62;
/** The toss: its speed and length, and how high the server flies it straight. */
const TOSS = ANTONIETTA.moveset.special1!.projectile!;
const TOSS_LENGTH = (TOSS.speed * TOSS.lifetime) / 30;
const TOSS_HEIGHT = TOSS.height!;
/**
 * Brioches still in the air. A new mine shows only its ring until they have
 * come down (the toss is drawn a little behind the server, the mine is not),
 * for at most `LANDING_SECONDS`.
 */
const inFlight = new Set<BriocheView>();
const LANDING_SECONDS = 1;

/** The brioche, upright and turned to the camera, as every sprite of the arena. */
function briocheSprite(scene: Scene, name: string): { mesh: Mesh; material: StandardMaterial; texture: Texture } {
  const mesh = CreatePlane(name, { size: BRIOCHE_SIZE }, scene);
  mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
  mesh.isPickable = false;
  const texture = new Texture("/proto2d/antonietta/brioche.png", scene, true, true, Texture.NEAREST_SAMPLINGMODE);
  texture.hasAlpha = true;
  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.disableLighting = true;
  material.emissiveColor = Color3.White();
  material.backFaceCulling = false;
  mesh.material = material;
  return { mesh, material, texture };
}

/**
 * Maria Antonietta's brioche in flight (D46). The server flies it straight,
 * high over the heads (it hits nobody in the air); here it rises from her
 * hand and falls in an arc, by the distance flown, to where it lands. The
 * shot disappears a little before the drawing gets there (it is drawn a few
 * steps late): the brioche then finishes its fall on its own.
 */
export class BriocheView {
  private readonly scene: Scene;
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private origin: { x: number; z: number } | null = null;
  private last = { x: 0, y: 0, z: 0, fx: 0, fz: 0 };
  private landing: Observer<Scene> | null = null;
  private released = false;

  constructor(scene: Scene) {
    this.scene = scene;
    const sprite = briocheSprite(scene, "brioche-toss");
    this.mesh = sprite.mesh;
    this.material = sprite.material;
    this.texture = sprite.texture;
    inFlight.add(this);
  }

  update(x: number, y: number, z: number, fx: number, fz: number, _dt: number): void {
    this.origin ??= { x, z };
    this.last = { x, y, z, fx, fz };
    this.place(x, y, z);
  }

  /** The hand, the top of the arc, the floor: by the share of the toss already flown. */
  private place(x: number, y: number, z: number): void {
    const t = this.origin ? Math.min(1, Math.hypot(x - this.origin.x, z - this.origin.z) / TOSS_LENGTH) : 0;
    this.mesh.position.set(x, y - TOSS_HEIGHT + 1.2 * (1 - t) + (BRIOCHE_SIZE / 2) * t + 1.8 * 4 * t * (1 - t), z);
  }

  dispose(): void {
    const origin = this.origin;
    if (!origin || this.landing) return this.release();
    let { x, z } = this.last;
    const { y, fx, fz } = this.last;
    let left = Math.max(0, TOSS_LENGTH - Math.hypot(x - origin.x, z - origin.z));
    this.landing = this.scene.onBeforeRenderObservable.add(() => {
      const step = Math.min(left, (TOSS.speed * this.scene.getEngine().getDeltaTime()) / 1000);
      left -= step;
      x += fx * step;
      z += fz * step;
      this.place(x, y, z);
      if (left <= 0) this.release();
    });
  }

  private release(): void {
    if (this.released) return;
    this.released = true;
    inFlight.delete(this);
    if (this.landing) this.scene.onBeforeRenderObservable.remove(this.landing);
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/**
 * A brioche waiting on the floor: its blast drawn as a faint ring, and a red
 * blink that quickens as the fuse runs out.
 */
export class BriocheMineView {
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private readonly ring: Mesh;
  private readonly ringMaterial: StandardMaterial;
  private age = 0;
  private landed = false;

  constructor(scene: Scene) {
    const sprite = briocheSprite(scene, "brioche-mine");
    this.mesh = sprite.mesh;
    this.material = sprite.material;
    this.texture = sprite.texture;
    this.ring = CreateDisc("brioche-blast", { radius: 1, tessellation: 40 }, scene);
    this.ring.rotation.x = Math.PI / 2;
    this.ring.isPickable = false;
    this.ringMaterial = new StandardMaterial("brioche-blast", scene);
    this.ringMaterial.disableLighting = true;
    this.ringMaterial.emissiveColor = new Color3(1, 0.62, 0.78);
    this.ringMaterial.alpha = 0.12;
    this.ringMaterial.backFaceCulling = false;
    this.ring.material = this.ringMaterial;
  }

  update(zone: ZoneState, dt: number): void {
    this.age += dt;
    this.landed ||= inFlight.size === 0 || this.age >= LANDING_SECONDS;
    this.mesh.isVisible = this.landed;
    this.mesh.position.set(zone.x, zone.y + BRIOCHE_SIZE / 2, zone.z);
    this.ring.position.set(zone.x, zone.y + 0.05, zone.z);
    this.ring.scaling.set(zone.radius, zone.radius, 1);
    // From one blink a second to six as the fuse burns down.
    const left = zone.ticks / ANTONIETTA_BRIOCHE.fuse;
    const blink = Math.sin(this.age * Math.PI * 2 * (1 + 5 * (1 - left))) > 0.4;
    this.material.emissiveColor.set(1, blink ? 0.45 : 1, blink ? 0.4 : 1);
    this.ringMaterial.alpha = blink ? 0.22 : 0.1;
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
    this.ring.dispose();
    this.ringMaterial.dispose();
  }
}
